import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Result, Spin } from "antd";
import LandToursApi from "../../../api-services/land-tours-api.service";
import { LandTourCategory, LandTourDetail } from "../../../interfaces/land-tour.interface";
import GroundTourHeader from "./components/GroundTourHeader";
import GroundTourGallery from "./components/GroundTourGallery";
import WhatsIncluded from "./components/WhatsIncluded";
import HighlightCard from "./components/HighlightCard";
import AboutTheTour from "./components/AboutTheTour";
import ChooseYourTour, { DateSelection, EMPTY_PAX_COUNTS, firstDateSelection, PaxCounts } from "./components/ChooseYourTour";
import GroundTourItinerary from "./components/GroundTourItinerary";
import GroundTourSidebar from "./components/GroundTourSidebar";
import GroundTourPriceBar from "./components/GroundTourPriceBar";
import useBreakpoint from "../../../utils/useBreakpoint";
import BookingNoticeTab from "../BookingNoticeTab";

const dateRulesOf = (tour: LandTourDetail) => ({ bookingCutOffDay: tour.bookingCutOffDay, departDay: tour.departDay });

// Ground Tour Itinerary Show Page (issue #629). Mirrors the Series show page's
// data-fetch shape (SeriesApi.getItineraryV2) via LandTourApi, but is kept as a
// thin composition of section components rather than ShowPage.tsx's single
// 2,500+ line file - there is no v1/v2/v3 layout-version split to support here.
export default function GroundTourShowPage() {
  const { code } = useParams<{ code: string }>();
  const isMobile = useBreakpoint();
  const [searchParams] = useSearchParams();
  const [landTour, setLandTour] = useState<LandTourDetail | null>(null);
  const [loading, setLoading] = useState(true);

  const [selectedCategory, setSelectedCategory] = useState<LandTourCategory | undefined>(undefined);
  const [selectedDate, setSelectedDate] = useState<DateSelection | undefined>(undefined);
  const [paxCounts, setPaxCounts] = useState<PaxCounts>(EMPTY_PAX_COUNTS);
  // Distinct from `!landTour` alone so the not-found state only shows after a real fetch
  // attempt - a missing/empty :code param renders nothing rather than a false "not found".
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!code) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setLoadFailed(false);

    LandToursApi
      .getLandTour(code, searchParams.get("ref"))
      .then((data) => {
        if (!active) return;
        setLandTour(data);

        const firstCategory = data.pricingCategories[0];
        setSelectedCategory(firstCategory);
        setSelectedDate(firstDateSelection(firstCategory, dateRulesOf(data)));
        setPaxCounts({ ...EMPTY_PAX_COUNTS, adult: Math.max(data.minBooking || 1, 1) });
      })
      .catch(() => {
        if (!active) return;
        setLandTour(null);
        setLoadFailed(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const handleCategoryChange = (category: LandTourCategory) => {
    setSelectedCategory(category);
    setSelectedDate(firstDateSelection(category, landTour ? dateRulesOf(landTour) : {}));
  };

  if (loading) {
    return (
      <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!landTour) {
    if (!loadFailed) return null;

    return (
      <Result
        status="404"
        title="Tour not found"
        subTitle="This tour may no longer be available, or the link may be incorrect."
        extra={<Link to="/web/ground-tour">Back to Ground Tours</Link>}
      />
    );
  }

  if (isMobile) {
    return (
      <div id="ground-tour-show-v2" className="ground-tour-show-v2--mobile">
        <GroundTourGallery images={landTour.images} />

        <div className="ground-tour-show-v2__m-body">
          <div className="ground-tour-show-v2__m-intro">
            <GroundTourHeader landTour={landTour} />
            <WhatsIncluded landTour={landTour} />
            <HighlightCard highlight={landTour.highlight} />
            <AboutTheTour landTour={landTour} />
          </div>

          <ChooseYourTour
            landTour={landTour}
            selectedCategory={selectedCategory}
            onCategoryChange={handleCategoryChange}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            paxCounts={paxCounts}
            onPaxCountsChange={setPaxCounts}
          />

          <GroundTourItinerary landTour={landTour} />

          <section className="ground-tour-show-v2__booking-notice">
            <h2 className="ground-tour-show-v2__section-title">Booking Notice</h2>
            <BookingNoticeTab removeHeader grouped />
          </section>
        </div>

        <GroundTourPriceBar
          landTourId={landTour.id}
          tourCode={landTour.code}
          destination={landTour.country}
          selectedCategory={selectedCategory}
          selectedDate={selectedDate}
          paxCounts={paxCounts}
          compulsoryCharges={landTour.compulsoryCharges}
        />
      </div>
    );
  }

  return (
    <div id="ground-tour-show-v2">
      <GroundTourHeader landTour={landTour} />

      <div className="ground-tour-show-v2__gallery-wrap">
        <GroundTourGallery images={landTour.images} />
      </div>

      <div className="ground-tour-show-v2__layout">
        <div className="ground-tour-show-v2__main">
          <WhatsIncluded landTour={landTour} />
          <HighlightCard highlight={landTour.highlight} />
          <AboutTheTour landTour={landTour} />
          <ChooseYourTour
            landTour={landTour}
            selectedCategory={selectedCategory}
            onCategoryChange={handleCategoryChange}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            paxCounts={paxCounts}
            onPaxCountsChange={setPaxCounts}
          />
          <GroundTourItinerary landTour={landTour} />

          <section className="ground-tour-show-v2__booking-notice">
            <h2 className="ground-tour-show-v2__section-title">Booking Notice</h2>
            {/* removeHeader: the section heading above already says "Booking Notice". */}
            <BookingNoticeTab removeHeader grouped />
          </section>
        </div>

        <GroundTourSidebar
          landTourId={landTour.id}
          tourCode={landTour.code}
          destination={landTour.country}
          selectedCategory={selectedCategory}
          selectedDate={selectedDate}
          paxCounts={paxCounts}
          compulsoryCharges={landTour.compulsoryCharges}
        />
      </div>
    </div>
  );
}
